import { useState, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { addPointFormSchema, type AddPointFormData } from '../types'
import { useBranchOffices } from '@/modules/clients/hooks/useBranchOffices'
import { useConfig } from '../hooks/useConfig'
import type { Client } from '@/modules/clients/types'

interface AddPointFormProps {
  client: Client
  onSubmit: (data: {
    id_client: number
    amount: number
    points: number
    transaction_type: 'add'
    id_branch_office: number
    comment: string
  }) => void
  isSubmitting: boolean
  error?: string | null
}

export function AddPointForm({ client, onSubmit, isSubmitting, error }: AddPointFormProps) {
  const { branches } = useBranchOffices()
  const { config } = useConfig()
  const [isDevolution, setIsDevolution] = useState(false)

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<AddPointFormData>({
    resolver: zodResolver(addPointFormSchema),
    defaultValues: {
      id_client: client.id,
      amount: 0,
      id_branch_office: client.id_branch_office,
      comment: '',
      is_devolution: false,
      points: 0,
    },
  })

  const watchedAmount = watch('amount')
  const watchedPoints = watch('points')

  const calculatedPoints = useMemo(() => {
    if (isDevolution) return watchedPoints || 0
    if (!config?.value_CLP_to_point || !watchedAmount) return 0
    return Math.floor(watchedAmount / config.value_CLP_to_point)
  }, [watchedAmount, watchedPoints, config, isDevolution])

  const handleFormSubmit = (data: AddPointFormData) => {
    onSubmit({
      id_client: client.id,
      amount: isDevolution ? 0 : data.amount,
      points: isDevolution ? (data.points ?? 0) : calculatedPoints,
      transaction_type: 'add',
      id_branch_office: data.id_branch_office,
      comment: data.comment ?? '',
    })
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6 max-w-xl">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Client info */}
      <div className="card p-4 bg-gray-50">
        <p className="text-sm text-gray-500">Cliente</p>
        <p className="font-semibold">
          {client.name} {client.last_name} ({client.rut})
        </p>
      </div>

      {/* Devolution toggle */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => { setIsDevolution(false); setValue('is_devolution', false) }}
          className={`btn ${!isDevolution ? 'btn-primary' : 'btn-secondary'}`}
        >
          Agregar Puntos
        </button>
        <button
          type="button"
          onClick={() => { setIsDevolution(true); setValue('is_devolution', true) }}
          className={`btn ${isDevolution ? 'btn-primary' : 'btn-secondary'}`}
        >
          Devolucion
        </button>
      </div>

      {!isDevolution ? (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Monto de compra (CLP) *
          </label>
          <input
            {...register('amount', { valueAsNumber: true })}
            type="number"
            min={0}
            className="input"
          />
          {errors.amount && (
            <p className="text-red-500 text-sm mt-1">{errors.amount.message}</p>
          )}
          <p className="text-sm text-gray-500 mt-2">
            Puntos a asignar: <span className="font-bold text-primary-600">{calculatedPoints}</span>
            {config && (
              <span className="ml-2">
                (${config.value_CLP_to_point.toLocaleString('es-CL')} CLP = 1 punto)
              </span>
            )}
          </p>
        </div>
      ) : (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Puntos a devolver *
          </label>
          <input
            {...register('points', { valueAsNumber: true })}
            type="number"
            min={0}
            className="input"
          />
          {errors.points && (
            <p className="text-red-500 text-sm mt-1">{errors.points.message}</p>
          )}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Sucursal *</label>
        <select
          {...register('id_branch_office', { valueAsNumber: true })}
          className="input"
        >
          <option value="">Seleccionar sucursal</option>
          {branches.map((branch) => (
            <option key={branch.id} value={branch.id}>
              {branch.commune} - {branch.address}
            </option>
          ))}
        </select>
        {errors.id_branch_office && (
          <p className="text-red-500 text-sm mt-1">{errors.id_branch_office.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Comentario</label>
        <textarea {...register('comment')} className="input" rows={3} />
      </div>

      <button type="submit" disabled={isSubmitting} className="btn btn-primary">
        {isSubmitting ? 'Registrando...' : 'Agregar Puntos'}
      </button>
    </form>
  )
}
